import { ReactNode } from 'react'
import { McisCutoverRollbackResendResponse, McisCutoverRollbackResendRow } from '@/console/api/types'
import { DataTable, DataTableColumn } from '@/console/ui/DataTable'
import { Tag } from '@/console/ui/Tag'
import { OUTCOME_PRESENTATION, ROW_STATUS, ROW_STATUS_PRESENTATION, ROW_TABLE_MAX_HEIGHT, countByRowStatus } from './mcisCutoverRollbackResend'
import styles from './McisCutoverRollbackResend.module.css'

/** 열 이름은 OIF_LO_GI 원문 컬럼명이다 — MCIS·SAP 담당자와 같은 이름으로 대사한다. */
const ROW_COLUMNS: DataTableColumn[] = [
  { key: 'status', label: '판정' },
  { key: 'id', label: 'OIF_LO_GI.id', align: 'right' },
  { key: 'clsDate', label: 'CLS_DATE', mono: true },
  { key: 'storeOrderNo', label: 'STORE_ORDER_NO', mono: true },
  { key: 'storeOrderSeq', label: 'STORE_ORDER_SEQ', mono: true },
  { key: 'sndSeq', label: 'SNDSEQ', mono: true },
  { key: 'claim', label: 'CLAIM', mono: true },
  { key: 'apiFlag', label: 'API_FLAG', mono: true },
  { key: 'piStat', label: 'PISTAT', mono: true },
  { key: 'ordRemark', label: 'ORD_REMARK', mono: true },
]

const valueOrDash = (value: string | null): string => value ?? '-'

const rowCells = (row: McisCutoverRollbackResendRow): Record<string, ReactNode> => {
  const presentation = ROW_STATUS_PRESENTATION[row.status]
  return {
    status: (
      <span className={styles.status}>
        <Tag tone={presentation.tone}>{presentation.label}</Tag>
        <span className={styles.meaning}>{presentation.meaning}</span>
      </span>
    ),
    id: row.id,
    clsDate: valueOrDash(row.clsDate),
    storeOrderNo: valueOrDash(row.storeOrderNo),
    storeOrderSeq: valueOrDash(row.storeOrderSeq),
    sndSeq: valueOrDash(row.sndSeq),
    claim: valueOrDash(row.claim),
    apiFlag: valueOrDash(row.apiFlag),
    piStat: valueOrDash(row.piStat),
    ordRemark: valueOrDash(row.ordRemark),
  }
}

export const ResendRowTable = ({ rows }: { rows: McisCutoverRollbackResendRow[] }): JSX.Element => (
  <DataTable
    maxHeight={ROW_TABLE_MAX_HEIGHT}
    stickyHeader
    columns={ROW_COLUMNS}
    rows={rows.map((row) => ({ key: String(row.id), muted: row.status === ROW_STATUS.ALREADY_RELEASED, cells: rowCells(row) }))}
  />
)

export const RowStatusCounts = ({ rows }: { rows: McisCutoverRollbackResendRow[] }): JSX.Element => (
  <div className={styles.counts}>
    {countByRowStatus(rows).map(({ status, count }) => (
      <Tag key={status} tone={ROW_STATUS_PRESENTATION[status].tone}>
        {ROW_STATUS_PRESENTATION[status].label} {count}건
      </Tag>
    ))}
  </div>
)

export const ResponseSummary = ({ response }: { response: McisCutoverRollbackResendResponse }): JSX.Element => {
  const outcome = OUTCOME_PRESENTATION[response.outcome]
  return (
    <div className={styles.summary}>
      <Tag tone={outcome.tone}>{outcome.label}</Tag>
      <span>요청 {response.requestedCount}건</span>
      <span>복원 대상 {response.eligibleCount}건</span>
      {response.dryRun ? null : <span>실제 복원 {response.releasedCount}건</span>}
      <span>컷오버 flag {response.cutoverEnabled ? 'ON' : 'OFF'}</span>
      <span>실행 {response.executeAllowed ? '허용' : '불가'}</span>
      <span>실행자 {response.operatorIdentity}</span>
    </div>
  )
}
